import * as React from 'react';

import { Rocket, X } from 'lucide-react';

import { Box, BoxProps, IconButton, Stack } from '@mui/material';

import { ButtonPrimary } from '@itp/talent/components/core/button';
import { BodyTextSmall, HeadingMedium } from '@itp/talent/components/core/typography';
import { formatDate } from '@itp/talent/utils/formatDate';

type BoostCardProps = {
  boostedUntil?: Date | null | string;
  description: string;
  loading?: boolean;
  onBoost: () => Promise<void> | void;
  onHide?: () => void;
  title: string;
} & BoxProps;

const BOOST_DAYS = 3;

export const BoostCard: React.FC<BoostCardProps> = ({
  boostedUntil,
  description,
  loading,
  onBoost,
  onHide,
  title,
  ...props
}) => {
  const [acting, setActing] = React.useState(false);

  const isActive = boostedUntil ? new Date(boostedUntil) > new Date() : false;

  const handleBoost = async () => {
    setActing(true);
    try {
      await onBoost();
    } finally {
      setActing(false);
    }
  };

  return (
    <Box
      sx={{
        background: 'linear-gradient(135deg, #18181b 0%, #27272a 100%)',
        border: '1px solid #3f3f46',
        borderRadius: 2,
        overflow: 'hidden',
        position: 'relative',
        px: 3,
        py: 2.5,
        ...props.sx,
      }}
    >
      <Box
        sx={{
          background:
            'radial-gradient(circle at top right, rgba(161,161,170,0.12) 0%, transparent 60%)',
          inset: 0,
          pointerEvents: 'none',
          position: 'absolute',
        }}
      />

      {onHide && (
        <IconButton
          sx={{
            '&:hover': { color: '#a1a1aa' },
            color: '#71717a',
            position: 'absolute',
            right: 4,
            top: 4,
          }}
          onClick={onHide}
          size="small"
        >
          <X size={14} />
        </IconButton>
      )}

      <Stack alignItems="flex-start" direction="column" gap={2} justifyContent="space-between">
        <Stack gap={0.75}>
          <Stack alignItems="center" direction="column" gap={1}>
            <Rocket color="#a1a1aa" size={36} />
          </Stack>
          <HeadingMedium sx={{ color: '#fafafa', fontSize: '1rem' }}>{title}</HeadingMedium>

          <BodyTextSmall sx={{ color: '#a1a1aa', maxWidth: 420 }}>
            {isActive
              ? `Active — visible boost until ${formatDate(boostedUntil)}. You can extend it once it expires.`
              : description}
          </BodyTextSmall>

          {!isActive && (
            <BodyTextSmall sx={{ color: '#71717a', mt: 0.25 }}>
              Free · {BOOST_DAYS} days · No credit card required
            </BodyTextSmall>
          )}
        </Stack>

        <ButtonPrimary
          sx={{
            '&:not(:disabled)': {
              '&:hover': {
                background: '#e4e4e7',
              },
              background: '#fafafa',
              color: '#18181b',
            },
            flexShrink: 0,
            fontWeight: 600,
            width: '100%',
          }}
          disabled={isActive || acting || loading}
          loading={acting || loading}
          onClick={handleBoost}
          size="small"
        >
          {isActive ? 'Boosted' : 'Boost now'}
        </ButtonPrimary>
      </Stack>
    </Box>
  );
};
